/**
 * NNADD Coordinates — a personalização na página de produto
 * (blocks/nnadd-coordinates.liquid, plano U2).
 *
 * O cliente procura o lugar no Nominatim, escolhe um resultado, pode afinar o
 * pin num mini-mapa e vê a t-shirt com o grafismo do peito. O botão de compra
 * só segue pela validação nativa do formulário (KTD4): o campo do lugar tem
 * `setCustomValidity()` enquanto não houver coordenadas, e a checkbox de
 * confirmação é `required`.
 *
 * Sem imports estáticos e sem importmap: o módulo de desenho
 * (assets/nnadd-coordinates-design.js) e o da pesquisa no Nominatim
 * (assets/nnadd-coordinates-pesquisa.js, partilhado com a secção da página
 * inicial) chegam por `import()` com o URL do CDN que o Liquid põe em
 * `data-desenho` e `data-pesquisa`, porque os assets têm impressão digital.
 *
 * Lugar vindo da página inicial: com `?lugar=…&lat=…&lng=…` no URL, o lugar
 * fica escolhido assim que o módulo de desenho carrega, como se viesse da
 * pesquisa, mas com a confirmação por marcar (`aplicarLugarDoUrl`).
 * O Leaflet 1.9.4 vem do cdnjs, e só quando o cliente abre "Ajustar no mapa".
 *
 * As coordenadas são arredondadas às 6 casas (`arredondar6`) ANTES de se
 * calcular o texto e a pré-visualização, para que o script de
 * tools/coordinates, ao recalcular a partir de `_lat` e `_lng`, chegue ao
 * mesmo texto (KTD6).
 *
 * Cor (U12, R33, R34, KTD15): a cor da camisola é a opção Color da variante
 * selecionada no seletor do tema. O Liquid emite em `data-produto` a cor de
 * cada variante, na língua servida; o valor converte-se numa chave de
 * `CAMISOLAS` (`resolverCor`). As mudanças chegam pelo evento
 * `shopify:product:select`, que o seletor de variantes lança em cada troca. A
 * cor do estampado escolhe-se aqui, só entre as `tintasPermitidas`, e vai para
 * `Estampado` (nome traduzido) e `_tinta` (chave).
 */

const LEAFLET_VERSION = '1.9.4';
const LEAFLET_BASE = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VERSION}`;
// Os mesmos hashes de assets/nnadd-coordinates-map.js.
const LEAFLET_JS_SRI =
  'sha512-BwHfrr4c9kmRkLw6iXFdzcdWV/PGkVgiIyIWLLlTSXzWQzxuSg4DiQUCpauz/EWjgk5TYQqX/kvn9pG1NpYfqg==';
const LEAFLET_CSS_SRI =
  'sha512-Zcn6bjR/8RZbLEpLIeOwNtzREBAJnUKESxces60Mpoj+2okopSAcSUIUOseddDm0cxnGQzxIR7vJgsLZbdLE3w==';
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const ZOOM_INICIAL = 14;

/** Nomes da opção da cor, como no bloco NNADD de snippets/product-media-gallery-content.liquid. */
const NOMES_OPCAO_COR = ['color', 'colour', 'cor', 'couleur', 'farbe', 'colore'];

/**
 * A Shopify traduz os valores das opções: na loja em /pt, "Black" chega como
 * "Preto". Além das chaves e dos nomes de `CAMISOLAS`, estes são os nomes
 * usados nas traduções da loja (tools/translations/ledger/produtos/_opcoes.json).
 * @type {Record<string, string[]>}
 */
const ALIASES_COR = {
  Black: ['Preto', 'Preta', 'Noir', 'Schwarz'],
  White: ['Branco', 'Branca', 'Blanc', 'Weiß', 'Weiss'],
  Sand: ['Areia', 'Sable', 'Sand'],
  Navy: ['Azul-marinho', 'Bleu marine', 'Marine', 'Marineblau', 'Navy blue'],
  'Military Green': ['Verde-militar', 'Vert militaire', 'Militärgrün', 'Military green'],
};

/** Um produto sem opção de cor é o da primeira versão, só preto. */
const COR_SEM_OPCAO = 'Black';

/** Para comparar nomes de cor: sem acentos, minúsculas, hífenes e espaços iguais. */
function normalizarNome(/** @type {unknown} */ valor) {
  return String(valor ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .trim();
}

/**
 * A chave de `CAMISOLAS` para o valor da opção Color (em qualquer das línguas
 * da loja), ou null.
 * @param {Record<string, { etiquetas: Record<string, string> }>} camisolas
 * @param {unknown} valor
 * @returns {string | null}
 */
function resolverCor(camisolas, valor) {
  const alvo = normalizarNome(valor);
  if (!alvo) return null;
  for (const [chave, camisola] of Object.entries(camisolas)) {
    const nomes = [chave, ...Object.values(camisola.etiquetas ?? {}), ...(ALIASES_COR[chave] ?? [])];
    if (nomes.some((nome) => normalizarNome(nome) === alvo)) return chave;
  }
  return null;
}

/** O id numérico de uma variante, venha como número, texto ou gid. */
function idVariante(/** @type {unknown} */ id) {
  return /(\d+)$/.exec(String(id ?? ''))?.[1] ?? '';
}

/** Passo do pin com as setas do teclado, em píxeis do ecrã (com Shift, maior). */
const PASSO_PX = 8;
const PASSO_PX_GRANDE = 48;

/** Normaliza a longitude para [-180, 180], para um pin arrastado para lá do antimeridiano. */
function normalizarLongitude(/** @type {number} */ lng) {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

/* ---------------------------------------------------------------------------
 * Leaflet, carregado uma vez por página.
 * ------------------------------------------------------------------------- */

/** @type {Promise<any> | null} */
let leafletPromise = null;

/**
 * Só resolve depois de a folha de estilos E o script carregarem: sem o CSS, o
 * Leaflet mede o contentor e põe os mosaicos no sítio errado. Se alguma das
 * duas falhar, retira as etiquetas que pôs, para a próxima tentativa não as
 * duplicar.
 */
function carregarLeaflet() {
  const w = /** @type {any} */ (window);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    const urlCss = `${LEAFLET_BASE}/leaflet.css`;
    /** @type {HTMLElement[]} */
    const injetados = [];
    let pendentes = 0;
    let terminado = false;

    const falhar = (/** @type {Error} */ erro) => {
      if (terminado) return;
      terminado = true;
      for (const el of injetados) el.remove();
      reject(erro);
    };
    const pronto = () => {
      if (terminado) return;
      pendentes -= 1;
      if (pendentes > 0) return;
      if (!w.L) {
        falhar(new Error('Leaflet em falta depois de carregar'));
        return;
      }
      terminado = true;
      resolve(w.L);
    };

    if (!document.querySelector(`link[rel="stylesheet"][href="${urlCss}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = urlCss;
      css.integrity = LEAFLET_CSS_SRI;
      css.crossOrigin = 'anonymous';
      css.referrerPolicy = 'no-referrer';
      css.onload = pronto;
      css.onerror = () => falhar(new Error('O CSS do Leaflet não carregou'));
      injetados.push(css);
      pendentes += 1;
    }

    if (!w.L) {
      const script = document.createElement('script');
      script.src = `${LEAFLET_BASE}/leaflet.js`;
      script.integrity = LEAFLET_JS_SRI;
      script.crossOrigin = 'anonymous';
      script.referrerPolicy = 'no-referrer';
      script.onload = pronto;
      script.onerror = () => falhar(new Error('O Leaflet não carregou'));
      injetados.push(script);
      pendentes += 1;
    }

    if (pendentes === 0) {
      // Já estavam os dois na página.
      pendentes = 1;
      pronto();
      return;
    }
    for (const el of injetados) document.head.append(el);
  }).catch((erro) => {
    leafletPromise = null; // deixa tentar outra vez mais tarde
    throw erro;
  });

  return leafletPromise;
}

/* ---------------------------------------------------------------------------
 * Pré-visualização: a t-shirt na cor escolhida e o grafismo do peito.
 * ------------------------------------------------------------------------- */

const SVG_NS = 'http://www.w3.org/2000/svg';

// A t-shirt é cromado da interface, não é a marca: uma silhueta plana de corte
// largo, em unidades do viewBox (400 de largura).
const LARGURA_TSHIRT = 400;
const ALTURA_TSHIRT = 440;
const SILHUETA =
  'M140 38 Q200 78 260 38 L334 58 L396 152 L344 182 L330 162 L334 430 L66 430 L70 162 L56 182 L4 152 L66 58 Z';
const GOLA = 'M140 38 Q200 78 260 38 Q200 92 140 38 Z';
// Uma t-shirt oversized tem cerca de 62 cm de peito; o corpo da silhueta mede 264 unidades.
const UNIDADES_POR_MM = 264 / 620;
// Peito esquerdo de quem a veste: à direita de quem olha.
const PEITO_X = 228;
const PEITO_Y = 104;
// O pormenor por baixo da t-shirt, com o grafismo ampliado.
const PORMENOR_LARGURA = 280;
const PORMENOR_MARGEM = 28;
const PORMENOR_TOPO = ALTURA_TSHIRT + 16;

// Medidas das linhas no módulo de desenho, para os marcadores tracejados antes
// de haver lugar: corpo 6 mm, maiúsculas a 0,698 do corpo, 3 mm abaixo do
// logótipo, 8 mm entre linhas de base.
const CORPO_MM = 6;
const MAIUSCULAS_MM = 0.698 * CORPO_MM;
const ESPACO_LOGO_MM = 3;
const ENTRELINHA_MM = 8;

/** Um logótipo vazio, para desenhar só as duas linhas enquanto falta o oficial. Não desenha nada. */
const SEM_LOGOTIPO = Object.freeze({ viewBox: '0 0 70 0', conteudo: '' });

const n = (/** @type {number} */ v) => String(Number(v.toFixed(3)));

/** @param {string} texto */
function escaparXml(texto) {
  return texto.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * O marcador antes de haver lugar: o logótipo oficial (se existir) e duas
 * linhas tracejadas onde vão ficar as coordenadas, sem algarismos, na tinta
 * escolhida.
 * @param {any} desenho módulo de desenho
 * @param {string} hex cor da tinta
 * @returns {string}
 */
function desenharMarcador(desenho, hex) {
  const largura = desenho.LARGURA_MM;
  const logo = desenho.LOGOTIPO;
  let alturaLogo = 0;
  let logoSvg = '';
  if (logo) {
    const [, , vbL, vbA] = String(logo.viewBox).trim().split(/[\s,]+/).map(Number);
    alturaLogo = (largura * vbA) / vbL;
    logoSvg = `<svg x="0" y="0" width="${largura}" height="${n(alturaLogo)}" viewBox="${logo.viewBox}">${logo.conteudo}</svg>`;
  }
  const base1 = alturaLogo + ESPACO_LOGO_MM + MAIUSCULAS_MM;
  const base2 = base1 + ENTRELINHA_MM;
  const meio = MAIUSCULAS_MM / 2;
  const altura = base2 + CORPO_MM * 0.05;
  return (
    `<svg xmlns="${SVG_NS}" viewBox="0 0 ${largura} ${n(altura)}">` +
    `<g fill="${hex}">${logoSvg}</g>` +
    `<g class="nnadd-perso__tracejado" fill="none" stroke="${hex}" stroke-width="0.9" stroke-dasharray="2.4 1.6">` +
    `<line x1="0" y1="${n(base1 - meio)}" x2="40" y2="${n(base1 - meio)}"/>` +
    `<line x1="0" y1="${n(base2 - meio)}" x2="36" y2="${n(base2 - meio)}"/>` +
    `</g></svg>`
  );
}

/**
 * Converte o SVG do peito (em texto) num elemento e posiciona-o.
 * @param {string} svgTexto
 * @param {number} x
 * @param {number} y
 * @param {number} largura
 * @returns {{ el: SVGSVGElement, altura: number }}
 */
function posicionar(svgTexto, x, y, largura) {
  const doc = new DOMParser().parseFromString(svgTexto, 'image/svg+xml');
  const el = /** @type {SVGSVGElement} */ (document.importNode(doc.documentElement, true));
  const [, , vbL, vbA] = (el.getAttribute('viewBox') ?? '0 0 1 1').trim().split(/[\s,]+/).map(Number);
  const altura = (largura * vbA) / vbL;
  el.setAttribute('x', n(x));
  el.setAttribute('y', n(y));
  el.setAttribute('width', n(largura));
  el.setAttribute('height', n(altura));
  el.setAttribute('aria-hidden', 'true');
  return { el, altura };
}

/* ---------------------------------------------------------------------------
 * O elemento.
 * ------------------------------------------------------------------------- */

class NnaddCoordinatesForm extends HTMLElement {
  /** @type {any} */ desenho = null;
  /** @type {Record<string, string>} */ textos = {};
  /** @type {Record<string, any>} */ refs = {};
  /** @type {{ lat: number, lng: number, local: string } | null} */ escolha = null;
  /** @type {{ lat: number, lng: number, local: string, detalhe: string }[]} */ resultados = [];
  /**
   * O módulo de pesquisa (assets/nnadd-coordinates-pesquisa.js) e o pedido ao
   * Nominatim em curso, que chegam por `import()` de `data-pesquisa`.
   * @type {any}
   */
  pesquisa = null;
  /** @type {any} */ pedidos = null;
  /** @type {Promise<any> | null} */ #pesquisaPromise = null;
  /** @type {any} */ mapa = null;
  /** @type {any} */ pin = null;
  aAjustar = false;
  semLogotipo = false;
  avisoLogotipo = false;
  falhouDesenho = false;
  /** Chave de `CAMISOLAS` da variante selecionada, ou null enquanto não se sabe. */
  /** @type {string | null} */ cor = null;
  /** O valor cru da opção Color, guardado até o módulo de desenho chegar. */
  /** @type {string | null} */ valorCor = null;
  /** Se o produto tem opção de cor e o valor selecionado não corresponde a nenhuma camisola. */
  corDesconhecida = false;
  /** Chave de `TINTAS` escolhida, ou null. */
  /** @type {string | null} */ tinta = null;
  /** @type {{ opcao: string, variante: string, cores: Record<string, string> }} */
  produto = { opcao: '', variante: '', cores: {} };
  /** @type {AbortController | null} */ #ouvintes = null;
  /** Os ouvintes ligam-se uma só vez, mesmo que o elemento saia e volte ao DOM. */
  #iniciado = false;

  connectedCallback() {
    this.connectedCallbackOuvintes();
    if (this.#iniciado) return;
    this.#iniciado = true;

    for (const el of this.querySelectorAll('[data-ref]')) {
      this.refs[/** @type {HTMLElement} */ (el).dataset.ref ?? ''] = el;
    }
    try {
      this.textos = JSON.parse(this.querySelector('script[data-textos]')?.textContent ?? '{}');
    } catch {
      this.textos = {};
    }
    try {
      const dados = JSON.parse(this.querySelector('script[data-produto]')?.textContent ?? '{}');
      this.produto = {
        opcao: typeof dados?.opcao === 'string' ? dados.opcao : '',
        variante: idVariante(dados?.variante),
        cores: dados?.cores && typeof dados.cores === 'object' ? dados.cores : {},
      };
    } catch {
      // Sem dados do produto: trata-se como produto sem opção de cor.
    }

    const { campo, procurar, lista, abrirMapa, ajustar, concluido, mapa, confirmar, publicar, tintasOpcoes } = this.refs;

    // A partir daqui a validade é gerida aqui. Antes do módulo de desenho
    // carregar, o campo continua inválido.
    campo.removeAttribute('pattern');
    campo.removeAttribute('required');
    this.atualizarValidade();

    campo.addEventListener('keydown', this.aoTeclarCampo);
    campo.addEventListener('input', this.aoEditarCampo);
    procurar.addEventListener('click', this.pesquisar);
    lista.addEventListener('click', this.aoClicarResultado);
    lista.addEventListener('keydown', this.aoTeclarLista);
    abrirMapa.addEventListener('click', this.alternarMapa);
    ajustar.addEventListener('click', () => this.definirAjuste(true));
    concluido.addEventListener('click', () => this.definirAjuste(false));
    mapa.addEventListener('keydown', this.aoTeclarMapa);
    confirmar.addEventListener('change', this.aoConfirmar);
    publicar.addEventListener('change', () => {
      this.refs.propPublicar.value = publicar.checked ? 'sim' : 'não';
    });
    this.refs.propPublicar.value = publicar.checked ? 'sim' : 'não';
    tintasOpcoes.addEventListener('change', this.aoEscolherTinta);
    this.aoConfirmar();
    this.valorCor = this.corDaVariante(this.varianteAtual()) ?? this.corDaVariante(this.produto.variante);

    // A pesquisa carrega já, em paralelo com o desenho, para o primeiro clique
    // em Procurar não esperar.
    this.carregarPesquisa();

    const url = this.dataset.desenho;
    if (!url) {
      this.falhouDesenho = true;
      this.atualizarValidade();
      this.mostrarEstado(this.textos.erroCarregar);
      return;
    }
    import(url)
      .then((modulo) => {
        this.desenho = modulo;
        this.refs.propDesenho.value = modulo.VERSAO_DESENHO;
        this.aplicarCor(this.valorCor, false);
        this.atualizarValidade();
        this.renderizarPrevia();
        this.aplicarLugarDoUrl();
        if (this.bloqueadoSemLogotipo()) {
          console.error('[nnadd-coordinates] Falta LOGOTIPO em nnadd-coordinates-design.js: compra bloqueada.');
          this.mostrarEstado(this.textos.erroCarregar);
        }
      })
      .catch(() => {
        this.desenho = null;
        this.falhouDesenho = true;
        this.mostrarEstado(this.textos.erroCarregar);
        this.atualizarValidade();
      });
  }

  /** O evento do seletor de variantes liga-se em cada entrada no DOM e desliga-se à saída. */
  connectedCallbackOuvintes() {
    this.#ouvintes?.abort();
    this.#ouvintes = new AbortController();
    document.addEventListener('shopify:product:select', this.aoMudarVariante, { signal: this.#ouvintes.signal });
  }

  disconnectedCallback() {
    this.#ouvintes?.abort();
    this.#ouvintes = null;
    this.mapa?.remove();
    this.mapa = null;
    this.pin = null;
    this.aAjustar = false;
    this.fecharMapa();
  }

  /* ----- pesquisa ----- */

  /**
   * O módulo de pesquisa, carregado uma vez. Resolve null se não carregar
   * (sem `data-pesquisa` ou falha de rede): a pesquisa mostra então o erro de
   * carregamento.
   * @returns {Promise<any>}
   */
  carregarPesquisa() {
    if (!this.#pesquisaPromise) {
      const url = this.dataset.pesquisa;
      this.#pesquisaPromise = (url ? import(url) : Promise.reject(new Error('Falta data-pesquisa')))
        .then((modulo) => {
          this.pesquisa = modulo;
          this.pedidos = new modulo.PesquisaLugares();
          return modulo;
        })
        .catch(() => {
          this.#pesquisaPromise = null; // deixa tentar outra vez no próximo clique
          return null;
        });
    }
    return this.#pesquisaPromise;
  }

  aoTeclarCampo = (/** @type {KeyboardEvent} */ evento) => {
    if (evento.key === 'Enter') {
      // Nunca submete o formulário de compra.
      evento.preventDefault();
      this.pesquisar();
    } else if (evento.key === 'ArrowDown' && !this.refs.resultados.hidden) {
      evento.preventDefault();
      this.botoesResultado()[0]?.focus();
    } else if (evento.key === 'Escape' && !this.refs.resultados.hidden) {
      this.esconderResultados();
    }
  };

  aoEditarCampo = () => {
    // O texto mudou: a pesquisa em curso já não é a deste texto.
    this.cancelarPesquisa();
    // Mudar o texto depois de escolher apaga as coordenadas confirmadas.
    if (this.escolha) this.limparPonto();
  };

  /** O texto do campo como vai para o Nominatim. */
  consultaAtual() {
    return this.refs.campo.value.trim().replace(/\s+/g, ' ');
  }

  /** Cancela o pedido em curso, se houver, e tira o estado de ocupado. */
  cancelarPesquisa() {
    if (!this.pedidos?.cancelar()) return;
    this.terminarOcupado();
    this.mostrarEstado('');
  }

  terminarOcupado() {
    this.refs.campo.removeAttribute('aria-busy');
    this.classList.remove('is-a-procurar');
  }

  pesquisar = async () => {
    const { campo } = this.refs;
    if (this.consultaAtual().length < 2) {
      this.cancelarPesquisa();
      this.mostrarEstado(this.textos.curto);
      campo.focus();
      return;
    }

    // O módulo costuma já estar carregado; se não, espera-se por ele antes de
    // ler o campo, e daqui até ao pedido corre tudo de seguida.
    const modulo = this.pesquisa ?? (await this.carregarPesquisa());
    if (!modulo) {
      this.mostrarEstado(this.textos.erroCarregar);
      return;
    }
    const consulta = this.consultaAtual();
    if (consulta.length < 2) return;

    const lingua = this.dataset.lingua || document.documentElement.lang || 'en';
    const chave = modulo.chavePesquisa(lingua, consulta);
    // Dois cliques (ou Enter e clique) na mesma consulta: um só pedido, como
    // pede a política do Nominatim.
    if (this.pedidos.emCurso(chave)) return;
    this.cancelarPesquisa();

    const resultado = await this.pedidos.procurar(consulta, lingua, {
      aoPedir: () => {
        // Os resultados à vista são da consulta anterior: saem antes do novo pedido.
        this.esconderResultados();
        campo.setAttribute('aria-busy', 'true');
        this.classList.add('is-a-procurar');
        this.mostrarEstado(this.textos.aProcurar);
      },
      aoTerminar: () => this.terminarOcupado(),
      // Só conta a resposta do último pedido, e só se o campo ainda diz o mesmo.
      valida: () => this.consultaAtual() === consulta,
    });
    if (!resultado) return;
    if ('erro' in resultado) {
      this.esconderResultados();
      this.mostrarEstado(this.textos.erroRede);
      return;
    }
    this.mostrarResultados(resultado.lista);
  };

  /** @param {{ lat: number, lng: number, local: string, detalhe: string }[]} lista */
  mostrarResultados(lista) {
    this.resultados = lista;
    const { resultados, lista: ul } = this.refs;
    if (!lista.length) {
      ul.replaceChildren();
      resultados.hidden = true;
      this.mostrarEstado(this.textos.semResultados);
      return;
    }
    this.pesquisa.renderizarResultados(ul, lista, 'nnadd-perso');
    resultados.hidden = false;
    this.mostrarEstado((this.textos.nResultados ?? '').replace('[n]', String(lista.length)));
  }

  esconderResultados() {
    this.refs.resultados.hidden = true;
    this.refs.lista.replaceChildren();
  }

  botoesResultado() {
    return /** @type {HTMLButtonElement[]} */ ([...this.refs.lista.querySelectorAll('button')]);
  }

  aoClicarResultado = (/** @type {MouseEvent} */ evento) => {
    const item = this.pesquisa?.lugarDoClique(evento, this.resultados);
    if (item) this.escolher(item);
  };

  aoTeclarLista = (/** @type {KeyboardEvent} */ evento) => {
    this.pesquisa?.teclarLista(evento, this.refs.lista, this.refs.campo, () => this.esconderResultados());
  };

  /* ----- lugar vindo da página inicial ----- */

  /**
   * `?lugar=…&lat=…&lng=…` (da secção sections/nnadd-coordinates-hero.liquid):
   * escolhe esse lugar como se viesse da pesquisa, com a confirmação por
   * marcar. Parâmetros inválidos ignoram-se em silêncio. A validação é
   * `lugarDoUrl`, do módulo de pesquisa, a mesma que monta o URL na secção.
   */
  async aplicarLugarDoUrl() {
    if (!/[?&]lugar=/.test(window.location.search)) return;
    const modulo = this.pesquisa ?? (await this.carregarPesquisa());
    const lugar = modulo?.lugarDoUrl(window.location.search);
    if (!lugar || !this.desenho || this.escolha) return;
    this.refs.campo.value = lugar.local;
    // definirPonto arredonda, preenche Local, Coordenadas, _lat e _lng,
    // desenha a pré-visualização, mostra "Ajustar no mapa" e anuncia o lugar
    // na região `estado` (aria-live).
    this.definirPonto(lugar.lat, lugar.lng, lugar.local);
  }

  /** @param {{ lat: number, lng: number, local: string }} item */
  escolher(item) {
    this.cancelarPesquisa();
    if (!this.desenho) {
      this.mostrarEstado(this.textos.erroCarregar);
      return;
    }
    // O valor do campo muda sem evento `input`, por isso não apaga a escolha.
    this.refs.campo.value = item.local;
    this.esconderResultados();
    this.definirPonto(item.lat, item.lng, item.local);
    this.refs.campo.focus();
    if (this.mapa && this.pin) {
      const { lat, lng } = /** @type {{ lat: number, lng: number }} */ (this.escolha);
      this.pin.setLatLng([lat, lng]);
      this.mapa.setView([lat, lng], ZOOM_INICIAL);
    }
  }

  /* ----- estado das coordenadas ----- */

  /**
   * Arredonda primeiro, depois calcula tudo a partir do valor arredondado.
   * @param {number} latBruta
   * @param {number} lngBruta
   * @param {string} local
   */
  definirPonto(latBruta, lngBruta, local) {
    const { arredondar6, formatarDMS } = this.desenho;
    const lat = arredondar6(Math.max(-90, Math.min(90, latBruta)));
    const lng = arredondar6(normalizarLongitude(lngBruta));
    const anterior = this.escolha;
    if (anterior && anterior.lat === lat && anterior.lng === lng && anterior.local === local) return;

    const { latitude, longitude, texto } = formatarDMS(lat, lng);
    this.escolha = { lat, lng, local };

    const r = this.refs;
    r.propLocal.value = local;
    r.propCoordenadas.value = texto;
    r.propLat.value = lat.toFixed(6);
    r.propLng.value = lng.toFixed(6);
    r.local.textContent = local;
    r.latitude.textContent = latitude;
    r.longitude.textContent = longitude;
    r.coordenadas.hidden = false;

    this.desmarcarConfirmacao();
    this.atualizarValidade();
    this.renderizarPrevia();
    this.mostrarEstado((this.textos.escolhido ?? '').replace('[l]', local).replace('[c]', texto));
  }

  limparPonto() {
    this.escolha = null;
    const r = this.refs;
    for (const campo of [r.propLocal, r.propCoordenadas, r.propLat, r.propLng]) campo.value = '';
    r.coordenadas.hidden = true;
    this.fecharMapa();
    this.desmarcarConfirmacao();
    this.atualizarValidade();
    this.renderizarPrevia();
  }

  /**
   * Qualquer mudança do lugar, do pin, da camisola ou da tinta desmarca a
   * confirmação e diz porquê (R4, R34).
   * @param {string | undefined} [texto]
   */
  desmarcarConfirmacao(texto = this.textos.mudou) {
    const { confirmar, mudou } = this.refs;
    if (confirmar.checked) {
      confirmar.checked = false;
      mudou.textContent = texto ?? '';
      mudou.hidden = false;
    }
    this.aoConfirmar();
  }

  aoConfirmar = () => {
    const { confirmar, mudou } = this.refs;
    confirmar.setCustomValidity(confirmar.checked ? '' : this.textos.faltaConfirmar ?? '');
    if (confirmar.checked) mudou.hidden = true;
  };

  /**
   * No editor de temas pode-se ver a pré-visualização sem o logótipo oficial;
   * na loja, não se vende. A nota `notaDev` só existe quando o Liquid corre com
   * `request.design_mode`.
   */
  emModoDesign() {
    const w = /** @type {any} */ (window);
    return Boolean(this.refs.notaDev) || Boolean(w.Shopify && w.Shopify.designMode);
  }

  /** Sem LOGOTIPO no módulo de desenho, fora do editor, a peça não pode ir para o carrinho. */
  bloqueadoSemLogotipo() {
    return Boolean(this.desenho) && !this.desenho.LOGOTIPO && !this.emModoDesign();
  }

  atualizarValidade() {
    const { campo } = this.refs;
    // Este é o único sítio que mexe na validade do campo.
    if (this.falhouDesenho || this.bloqueadoSemLogotipo() || this.corDesconhecida) {
      campo.setCustomValidity(this.textos.erroCarregar || ' ');
    } else if (!this.desenho || !this.escolha) campo.setCustomValidity(this.textos.faltaLugar || ' ');
    else campo.setCustomValidity('');
  }

  /** @param {string | undefined} texto */
  mostrarEstado(texto) {
    this.refs.estado.textContent = texto ?? '';
  }

  /* ----- cor da camisola e do estampado ----- */

  /** O id da variante selecionada: o do formulário de compra, ou o do Liquid. */
  varianteAtual() {
    const form = this.dataset.form ? document.getElementById(this.dataset.form) : null;
    const campoId = form instanceof HTMLFormElement ? form.elements.namedItem('id') : null;
    const valor = campoId instanceof HTMLInputElement || campoId instanceof HTMLSelectElement ? campoId.value : '';
    return idVariante(valor) || this.produto.variante;
  }

  /**
   * O valor da opção Color de uma variante, ou null se o produto não tem essa
   * opção (então é preto) ou a variante não é conhecida.
   * @param {string} id
   * @returns {string | null}
   */
  corDaVariante(id) {
    const cores = this.produto.cores;
    return Object.prototype.hasOwnProperty.call(cores, id) ? String(cores[id]) : null;
  }

  /** Se o produto tem opção de cor. */
  temOpcaoCor() {
    return Object.keys(this.produto.cores).length > 0;
  }

  /**
   * Muda a camisola. Mantém a tinta se for permitida; se não, passa à tinta
   * por omissão da nova cor (R34).
   * @param {string | null} valor valor da opção Color, na língua servida
   * @param {boolean} mudanca se é uma troca feita pelo cliente (desmarca a confirmação)
   */
  aplicarCor(valor, mudanca) {
    this.valorCor = valor;
    const desenho = this.desenho;
    if (!desenho) return;

    let cor = null;
    if (valor !== null) cor = resolverCor(desenho.CAMISOLAS, valor);
    else if (!this.temOpcaoCor()) cor = COR_SEM_OPCAO;
    // Com opção de cor, uma variante sem cor conhecida guarda a camisola anterior.
    else cor = this.cor;

    if (cor === null && valor !== null) {
      console.error(`[nnadd-coordinates] Cor de camisola desconhecida: ${JSON.stringify(valor)}. Compra bloqueada.`);
    }
    this.corDesconhecida = cor === null;
    if (cor === this.cor && this.tinta !== null) {
      this.atualizarValidade();
      return;
    }

    const anterior = this.cor;
    const tintaAnterior = this.tinta;
    this.cor = cor;
    this.tinta = cor === null ? null : desenho.tintaValida(cor, this.tinta) ? this.tinta : desenho.tintaPorOmissao(cor);
    this.renderizarTintas();
    this.atualizarPropsTinta();
    if (mudanca && (anterior !== cor || tintaAnterior !== this.tinta)) this.desmarcarConfirmacao(this.textos.mudouCor);
    this.atualizarValidade();
    this.renderizarPrevia();
    if (this.corDesconhecida) this.mostrarEstado(this.textos.erroCarregar);
  }

  /** O nome na língua servida, numa tabela de nomes por língua do módulo de desenho. */
  etiqueta(/** @type {Record<string, string> | undefined} */ etiquetas) {
    if (!etiquetas) return '';
    const lingua = this.dataset.lingua || 'en';
    const base = lingua.split('-')[0];
    const chave =
      etiquetas[lingua] !== undefined ? lingua : Object.keys(etiquetas).find((k) => k.split('-')[0] === base) ?? 'en';
    return etiquetas[chave] ?? '';
  }

  /** O nome dentro de uma frase: em minúsculas, menos em alemão. */
  noTexto(/** @type {string} */ nome) {
    const lingua = this.dataset.lingua || 'en';
    return lingua.startsWith('de') ? nome : nome.toLocaleLowerCase(lingua);
  }

  /** Os rádios da cor do estampado, só com as tintas permitidas na camisola. */
  renderizarTintas() {
    const { tintas, tintasOpcoes } = this.refs;
    const desenho = this.desenho;
    if (!desenho || !tintas) return;
    const permitidas = this.cor ? desenho.tintasPermitidas(this.cor) : [];
    tintas.hidden = permitidas.length === 0;
    tintas.style.setProperty('--nnadd-coord-amostra-fundo', this.cor ? desenho.CAMISOLAS[this.cor].hex : 'transparent');

    const nome = `${this.refs.campo.id}-tinta`;
    const tinhaFoco = tintasOpcoes.contains(document.activeElement);
    tintasOpcoes.replaceChildren(
      ...permitidas.map((/** @type {string} */ chave) => {
        const rotulo = document.createElement('label');
        rotulo.className = 'nnadd-perso__tinta';
        const entrada = document.createElement('input');
        entrada.type = 'radio';
        entrada.name = nome;
        entrada.value = chave;
        entrada.className = 'nnadd-perso__tinta-entrada';
        entrada.checked = chave === this.tinta;
        const amostra = document.createElement('span');
        amostra.className = 'nnadd-perso__amostra';
        amostra.setAttribute('aria-hidden', 'true');
        amostra.style.setProperty('--nnadd-coord-amostra', desenho.TINTAS[chave].hex);
        const texto = document.createElement('span');
        texto.textContent = this.etiqueta(desenho.TINTAS[chave].etiquetas);
        rotulo.append(entrada, amostra, texto);
        return rotulo;
      })
    );
    // A lista mudou com o foco lá dentro: o foco fica na tinta selecionada.
    if (tinhaFoco) tintasOpcoes.querySelector('input:checked')?.focus();
  }

  atualizarPropsTinta() {
    const { propTinta, propEstampado } = this.refs;
    const tinta = this.tinta && this.desenho ? this.desenho.TINTAS[this.tinta] : null;
    propTinta.value = tinta ? /** @type {string} */ (this.tinta) : '';
    propEstampado.value = tinta ? this.etiqueta(tinta.etiquetas) : '';
  }

  aoEscolherTinta = (/** @type {Event} */ evento) => {
    const entrada = evento.target;
    if (!(entrada instanceof HTMLInputElement) || entrada.type !== 'radio' || !entrada.checked) return;
    if (!this.desenho || !this.cor || !this.desenho.tintaValida(this.cor, entrada.value)) return;
    if (entrada.value === this.tinta) return;
    this.tinta = entrada.value;
    this.atualizarPropsTinta();
    this.desmarcarConfirmacao(this.textos.mudouCor);
    this.renderizarPrevia();
    this.levarGaleriaATinta(false);
  };

  /* ----- galeria do produto ----- */

  /**
   * O texto que marca a tinta no alt das fotos do produto ("white print",
   * "gold print", "black print", "burgundy print"): o nome inglês da tinta em
   * `TINTAS`, como nos alts escritos por tools/coordinates. Os alts das fotos
   * não são traduzidos, por isso o marcador é o mesmo em todas as línguas.
   */
  marcadorTinta() {
    const en = this.tinta && this.desenho ? this.desenho.TINTAS[this.tinta]?.etiquetas?.en : '';
    return en ? `${String(en).toLowerCase()} print` : '';
  }

  /** A galeria do produto desta secção (nem a dos cartões de produto nem a dos diálogos). */
  galeria() {
    const raiz = this.closest('.shopify-section') ?? document;
    return [...raiz.querySelectorAll('media-gallery')].find((g) => !g.closest('product-card, dialog')) ?? null;
  }

  /**
   * Um passo de levar a galeria à primeira foto da tinta escolhida. Só procura
   * nas fotos que o filtro NNADD da galeria deixou (as da cor selecionada).
   * - Carrossel (telemóvel, ou apresentação "carousel"): `select(índice)` do
   *   `slideshow-component` do tema, sobre `slides` (os slides visíveis).
   * - Grelha (computador): o slideshow está escondido; faz scroll até à foto
   *   se ela não estiver toda à vista (a coluna dos detalhes é fixa).
   * @param {boolean} soForaDaPrimeira só age se a foto não for a primeira (depois
   *   de uma troca de cor a galeria nova já começa na primeira)
   * @returns {'fim' | 'ok' | 'esperar'} 'fim': nada a fazer (ou o scroll da grelha
   *   já foi pedido); 'ok': o slideshow mostra a foto; 'esperar': o slideshow
   *   ainda não está pronto ou a seleção ainda não se viu em `current`
   */
  passoGaleria(soForaDaPrimeira) {
    const marcador = this.marcadorTinta();
    const galeria = /** @type {any} */ (this.galeria());
    if (!marcador || !galeria) return 'fim';
    const temTinta = (/** @type {Element} */ el) =>
      [...el.querySelectorAll('img[alt]')].some((img) => /** @type {HTMLImageElement} */ (img).alt.toLowerCase().includes(marcador));

    const slideshow = galeria.slideshow ?? galeria.querySelector('slideshow-component');
    if (slideshow && slideshow.getClientRects().length > 0) {
      /** @type {Element[]} */
      const slides = slideshow.slides ?? [];
      if (!slides.length) return 'esperar';
      const indice = slides.findIndex(temTinta);
      if (indice < 0 || (soForaDaPrimeira && indice === 0)) return 'fim';
      if (slideshow.current === indice) return 'ok';
      // Antes de o slideshow estar montado, select() não faz nada: tenta-se no frame seguinte.
      slideshow.select(indice);
      return 'esperar';
    }

    /** @type {Element[]} */
    const itens = galeria.media?.length ? galeria.media : [...galeria.querySelectorAll('.media-gallery__grid > li')];
    const indice = itens.findIndex(temTinta);
    if (indice < 0 || (soForaDaPrimeira && indice === 0)) return 'fim';
    const caixa = itens[indice].getBoundingClientRect();
    if (caixa.height === 0) return 'esperar';
    if (caixa.top >= 0 && caixa.bottom <= window.innerHeight) return 'fim';
    const reduzir = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    itens[indice].scrollIntoView({ block: 'nearest', behavior: reduzir ? 'auto' : 'smooth' });
    return 'fim';
  }

  /** @type {number} */ #vezGaleria = 0;

  /**
   * Leva a galeria à foto da tinta escolhida, frame a frame até o slideshow do
   * tema aceitar a seleção: depois de uma troca de variante a galeria é
   * substituída e o slideshow novo monta-se de forma assíncrona. No carrossel
   * fica feito quando `current` mostra a foto em 5 frames seguidos (o arranque
   * do slideshow pode voltar ao 0); na grelha, ao primeiro scroll. Desiste ao
   * fim de 2 s. Um pedido novo cancela o anterior.
   * @param {boolean} soForaDaPrimeira ver `passoGaleria`
   */
  levarGaleriaATinta(soForaDaPrimeira) {
    const vez = ++this.#vezGaleria;
    const limite = performance.now() + 2000;
    let seguidos = 0;
    const passo = () => {
      if (vez !== this.#vezGaleria || !this.isConnected || performance.now() > limite) return;
      const estado = this.passoGaleria(soForaDaPrimeira);
      if (estado === 'fim') return;
      seguidos = estado === 'ok' ? seguidos + 1 : 0;
      if (seguidos < 5) requestAnimationFrame(passo);
    };
    requestAnimationFrame(passo);
  }

  /**
   * Troca de variante no seletor do tema. A cor lê-se logo do `variantId` da
   * opção escolhida (ou de `selectedOptions`) e confirma-se quando o tema
   * recebe a variante resolvida.
   * @param {Event} evento `shopify:product:select`
   */
  aoMudarVariante = (evento) => {
    const origem = evento.target instanceof Element ? evento.target : null;
    if (!origem || origem.closest('product-card, quick-add-dialog, dialog')) return;
    const seccao = this.closest('.shopify-section');
    if (seccao && !seccao.contains(origem)) return;

    const e = /** @type {any} */ (evento);
    const id = idVariante(e.detail?.variantId);
    let valor = id ? this.corDaVariante(id) : null;
    if (valor === null && Array.isArray(e.selectedOptions)) {
      const nomeOpcao = normalizarNome(this.produto.opcao);
      const opcao = e.selectedOptions.find(
        (/** @type {{ name: string }} */ o) =>
          normalizarNome(o?.name) === nomeOpcao || NOMES_OPCAO_COR.includes(normalizarNome(o?.name))
      );
      if (opcao && typeof opcao.value === 'string') valor = opcao.value;
    }
    if (valor !== null) this.aplicarCor(valor, true);

    Promise.resolve(e.promise)
      .then((/** @type {any} */ resultado) => {
        if (!this.isConnected) return;
        const resolvida = idVariante(resultado?.detail?.resource?.id ?? resultado?.variant?.id);
        const cor = resolvida ? this.corDaVariante(resolvida) : null;
        if (cor !== null) this.aplicarCor(cor, true);
        // A galeria da variante nova (só com a cor nova) começa na primeira foto,
        // a da tinta por omissão; se a tinta escolhida for outra, vai até ela.
        this.levarGaleriaATinta(true);
      })
      .catch(() => {
        // Pedido cancelado por outra troca: a troca seguinte trata da cor.
      });
  };

  /* ----- pré-visualização ----- */

  renderizarPrevia() {
    const desenho = this.desenho;
    const figura = this.refs.previa;
    if (!desenho || !figura) return;

    /** @type {string} */
    let peito;
    let rotulo = this.textos.previaVazia ?? '';
    this.semLogotipo = !desenho.LOGOTIPO;
    // Sem cor conhecida não há peça para mostrar.
    if (!this.cor || !this.tinta) {
      figura.querySelector('svg.nnadd-perso__svg')?.remove();
      return;
    }
    const camisola = desenho.CAMISOLAS[this.cor];
    const tinta = desenho.TINTAS[this.tinta];
    if (this.escolha) {
      const { lat, lng } = this.escolha;
      try {
        peito = desenho.desenharPeito(lat, lng, { tinta: this.tinta });
      } catch (erro) {
        if (!/log[oó]tipo/i.test(String(/** @type {Error} */ (erro)?.message))) {
          console.error('[nnadd-coordinates] O desenho do peito falhou:', erro);
          return;
        }
        // Falta o SVG oficial: mostra só as duas linhas. Nunca se desenha um logótipo.
        peito = desenho.desenharPeito(lat, lng, { logotipo: SEM_LOGOTIPO, tinta: this.tinta });
      }
      rotulo = (this.textos.previaCom ?? '').replace('[c]', desenho.formatarDMS(lat, lng).texto);
    } else {
      peito = desenharMarcador(desenho, tinta.hex);
    }
    rotulo = rotulo
      .replace('[t]', this.noTexto(this.etiqueta(camisola.etiquetas)))
      .replace('[e]', this.noTexto(this.etiqueta(tinta.etiquetas)));

    if (this.semLogotipo && !this.avisoLogotipo) {
      this.avisoLogotipo = true;
      console.warn('[nnadd-coordinates] Pré-visualização sem logótipo: falta LOGOTIPO em nnadd-coordinates-design.js.');
      if (this.refs.notaDev) this.refs.notaDev.hidden = false;
    }

    const largura = desenho.LARGURA_MM * UNIDADES_POR_MM;
    const noPeito = posicionar(peito, PEITO_X, PEITO_Y, largura);
    const ampliado = posicionar(
      peito,
      (LARGURA_TSHIRT - PORMENOR_LARGURA) / 2,
      PORMENOR_TOPO + PORMENOR_MARGEM,
      PORMENOR_LARGURA
    );
    const alturaPormenor = ampliado.altura + PORMENOR_MARGEM * 2;
    const alturaTotal = PORMENOR_TOPO + alturaPormenor;

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', `0 0 ${LARGURA_TSHIRT} ${n(alturaTotal)}`);
    svg.setAttribute('class', 'nnadd-perso__svg');
    svg.setAttribute('role', 'img');
    svg.setAttribute('focusable', 'false');
    const idTitulo = `${this.refs.campo.id}-previa`;
    svg.setAttribute('aria-labelledby', idTitulo);
    svg.dataset.camisola = camisola.tom;
    svg.style.setProperty('--nnadd-coord-tee', camisola.hex);
    svg.style.setProperty('--nnadd-coord-detail-bg', camisola.hex);
    svg.innerHTML =
      `<title id="${idTitulo}">${escaparXml(rotulo)}</title>` +
      `<path class="nnadd-perso__tshirt" d="${SILHUETA}"/>` +
      `<path class="nnadd-perso__gola" d="${GOLA}"/>` +
      `<rect class="nnadd-perso__pormenor" x="0" y="${PORMENOR_TOPO}" width="${LARGURA_TSHIRT}" height="${n(alturaPormenor)}"/>`;
    svg.append(noPeito.el, ampliado.el);

    figura.querySelector('svg.nnadd-perso__svg')?.remove();
    figura.prepend(svg);
    this.classList.toggle('tem-lugar', Boolean(this.escolha));
  }

  /* ----- mini-mapa ----- */

  alternarMapa = () => {
    if (this.refs.painelMapa.hidden) this.abrirMapa();
    else this.fecharMapa();
  };

  abrirMapa() {
    if (!this.escolha) return;
    const { painelMapa, abrirMapa, mapaErro } = this.refs;
    painelMapa.hidden = false;
    abrirMapa.setAttribute('aria-expanded', 'true');
    abrirMapa.textContent = this.textos.fecharMapa ?? abrirMapa.textContent;
    mapaErro.hidden = true;

    carregarLeaflet()
      .then((L) => {
        if (!this.isConnected || painelMapa.hidden || !this.escolha) return;
        this.criarMapa(L);
      })
      .catch(() => {
        mapaErro.textContent = this.textos.mapaErro ?? '';
        mapaErro.hidden = false;
      });
  }

  fecharMapa() {
    const { painelMapa, abrirMapa } = this.refs;
    if (painelMapa.hidden) return;
    this.definirAjuste(false);
    painelMapa.hidden = true;
    abrirMapa.setAttribute('aria-expanded', 'false');
    abrirMapa.textContent = this.textos.ajustarMapa ?? abrirMapa.textContent;
  }

  /** @param {any} L */
  criarMapa(L) {
    const { lat, lng } = /** @type {{ lat: number, lng: number }} */ (this.escolha);
    if (this.mapa) {
      this.mapa.invalidateSize();
      this.pin.setLatLng([lat, lng]);
      this.mapa.setView([lat, lng], this.mapa.getZoom() || ZOOM_INICIAL);
      return;
    }
    // Fechado por omissão: sem arrastar, sem pinça e sem roda, para o dedo
    // que passa por cima do mapa continuar a fazer scroll à página.
    const mapa = L.map(this.refs.mapa, {
      dragging: false,
      touchZoom: false,
      scrollWheelZoom: false,
      keyboard: false,
      tap: false,
      boxZoom: false,
    });
    mapa.attributionControl.setPrefix(false);
    L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(mapa);
    mapa.setView([lat, lng], ZOOM_INICIAL);

    const icone = L.divIcon({ className: 'nnadd-perso__pin', iconSize: [24, 24], iconAnchor: [12, 12] });
    const pin = L.marker([lat, lng], { icon: icone, draggable: false, keyboard: false, autoPan: true }).addTo(mapa);
    pin.on('dragend', () => this.aoMoverPin(pin.getLatLng()));
    mapa.on('click', (/** @type {any} */ e) => {
      if (!this.aAjustar) return;
      pin.setLatLng(e.latlng);
      this.aoMoverPin(e.latlng);
    });

    this.mapa = mapa;
    this.pin = pin;
  }

  /** @param {boolean} ligado */
  definirAjuste(ligado) {
    const { ajustar, concluido } = this.refs;
    this.aAjustar = ligado && Boolean(this.mapa);
    if (this.mapa) {
      for (const handler of [this.mapa.dragging, this.mapa.touchZoom, this.pin?.dragging]) {
        if (!handler) continue;
        if (this.aAjustar) handler.enable();
        else handler.disable();
      }
    }
    this.refs.mapa.classList.toggle('is-a-ajustar', this.aAjustar);
    const tinhaFoco = this.contains(document.activeElement) && (document.activeElement === ajustar || document.activeElement === concluido);
    ajustar.hidden = this.aAjustar;
    concluido.hidden = !this.aAjustar;
    if (tinhaFoco) (this.aAjustar ? concluido : ajustar).focus();
  }

  /** @param {{ lat: number, lng: number }} latlng */
  aoMoverPin(latlng) {
    if (!this.escolha) return;
    this.definirPonto(latlng.lat, latlng.lng, this.escolha.local);
  }

  aoTeclarMapa = (/** @type {KeyboardEvent} */ evento) => {
    if (!this.mapa || !this.pin) return;
    const passo = evento.shiftKey ? PASSO_PX_GRANDE : PASSO_PX;
    /** @type {Record<string, [number, number]>} */
    const direcoes = { ArrowUp: [0, -passo], ArrowDown: [0, passo], ArrowLeft: [-passo, 0], ArrowRight: [passo, 0] };
    const delta = direcoes[evento.key];
    if (!delta) return;
    evento.preventDefault();
    const ponto = this.mapa.latLngToContainerPoint(this.pin.getLatLng());
    const novo = this.mapa.containerPointToLatLng([ponto.x + delta[0], ponto.y + delta[1]]);
    this.pin.setLatLng(novo);
    if (!this.mapa.getBounds().pad(-0.1).contains(novo)) this.mapa.panTo(novo);
    this.aoMoverPin(novo);
  };
}

if (!customElements.get('nnadd-coordinates-form')) {
  customElements.define('nnadd-coordinates-form', NnaddCoordinatesForm);
}
